import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, ActiveButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import {
  getLocationList,
  getLocationStatusCount,
  deactivateLocation,
  activeLocation,
} from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { useTeamValue } from "providers/TeamContext";
import { CSVLink } from "react-csv";
import { useCustomerValue } from "providers/CustomerContext";
import { ExportDialog } from "ui-components/Alert";
import dayjs from "dayjs";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { downloadExcel } from "layouts/views/common/file";

function LocationList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries("location-list");
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const [isExporting, setIsExporting] = useState(false)

  const addPermission =
    allowAll ||
    (Boolean(permissions["Location"]) &&
      permissions["Location"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Location"]) &&
      permissions["Location"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Location"]) &&
      permissions["Location"].findIndex((b) => b === "deactivate") !== -1);
  const [filteredData, setFilteredData] = useState([]);

  useQuery({
    queryKey: ["location-list", { customerCode, active }],
    queryFn: () => getLocationList({ customerCode, active }),
    onSuccess: (data) => {
      setFilteredData(data.data.data);
      setData(data.data.data);
    },
    enabled: Boolean(customerCode),
    onError,
  });

  useQuery({
    queryKey: ["location-list", "status", { customerCode }],
    queryFn: () => getLocationStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    enabled: Boolean(customerCode),
    onError,
  });

  const delMutation = useMutation(deactivateLocation, { onSuccess, onError });
  const activeMutation = useMutation(activeLocation, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/location/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.locationID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.locationID}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Location ID", accessorKey: "locationID" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Zone ID", accessorKey: "zoneID" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { locationID, warehouseCode, zoneID } = row.original;
        const state = { locationID, warehouseCode, zoneID };
        if (active) {
          return (
            <>
              <EditButton
                title="edit location"
                disabled={!editPermission}
                onClick={() => onEdit(state)}
              />
              <DelButton
                title="deactivate location"
                disabled={!deactivatePermission}
                onClick={() => onDelete(state)}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate location"
              disabled={!editPermission}
              onClick={() => onActive(state)}
            />
          );
        }
      },
    },
  ];

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const exportData = filteredData.map(loc => {
    return {
      ["Warehouse Code"]: loc.warehouseCode,
      ["Zone ID"]: loc.zoneID,
      ["Location ID"]: loc.locationID,
      ["Location Group"]: loc.locDimGrp,
      ["Description 1"]: loc.descriptions1,
      ["Description 2"]: loc.descriptions2,
      ["Aisle"]: loc.aisle,
      ["Rack"]: loc.rack,
      ["Level"]: loc.lLevel,
      ["Bay"]: loc.bay,
      ["Slot"]: loc.slot,
      ["Movement Type"]: loc.movementType,
      ["Location Type"]: loc.locationType,
      ["Small Pallet"]: loc.smallPallet,
      ["Standard Pallet"]: loc.standardPallet,
      ["Large Pallet"]: loc.largePallet,
      ["Mix Dates"]: loc.mixDates === true ? 1 : 0,
      ["Mix MLN"]: loc.mixMLN === true ? 1 : 0,
      ["Mix OLN"]: loc.mixOLN === true ? 1 : 0,
      ["Mix Part"]: loc.mixPart === true ? 1 : 0,
      ["Mix Vendor"]: loc.mixVendor === true ? 1 : 0,
      ["Mix Customer"]: loc.mixCustomer === true ? 1 : 0,
      ["Open Location"]: loc.openLoc === true ? 1 : 0,
      ["Active"]: status === "Active" ? 1 : 0,
    }
  })

  return (
    <ListLayout>
      {(isDownloading || isExporting) && <ExportDialog />}
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDBox>
            <MDButton
              variant="gradient"
              color="info"
              disabled={!addPermission}
              onClick={() => navigate("/setup/location/add")}
            >
              <Icon>add</Icon>&nbsp;location
            </MDButton>
            <MDButton
              sx={{ marginLeft: "1rem" }}
              variant="outlined"
              color="dark"
              disabled={!addPermission}
              onClick={() => navigate("/setup/location/upload")}
            >
              <Icon>upload</Icon> &nbsp;upload
            </MDButton>
          </MDBox>
          <MDBox alignItems="flex-end">
            <MDButton
              sx={{ marginRight: "1rem" }}
              variant="outlined"
              color="dark"
              disabled={!editPermission}
              onClick={() => navigate("/setup/location/bulk-update")}
            >
              <Icon>upload</Icon> &nbsp;bulk&nbsp;update
            </MDButton>
            {/* <CSVLink filename="ExportLocation_" data={exportData}>
              <MDButton variant="outlined" color="dark" title="export location">
                <Icon>download</Icon>&nbsp;export
              </MDButton>
            </CSVLink> */}
            {/* <MDButton
              variant="outlined" color="dark" title="export location"
              onClick={() => {
                const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_ExportLocation`;
                downloadCsv(filename, exportData)
              }}
            > 
              Export
            </MDButton> */}
            <MDButton
              variant="outlined"
              color="dark"
              title="export location"
              onClick={async () => {
                try {
                  const filename = `${customerCode}_${dayjs().format("YYYYMMDD-HHmm")}_ExportLocation`;
                  setIsExporting(true)
                  await downloadExcel(filename, exportData);
                } catch(error) {
                  console.log(error)
                }
                setIsExporting(false)
              }}
            >
              <Icon>download</Icon>&nbsp;export
            </MDButton>
          </MDBox>
        </MDBox>
        <BasicTable columns={columns} data={data} onFilterRowsChange={handleFilterRowsChange} />
      </TableCard>
    </ListLayout>
  );
}

export default LocationList;
